import { memo, useMemo, useState } from 'react';
import { useOnValueChange } from '../../../hooks/useOnValueChange';
import FilterIcon from '../../../assets/icons/filter.svg?react';
import { T } from '../../../translation/src';
import { Popover } from '../../../components/Popover';
import { AnnotationType } from '../../Properties/types';
import { getAnnotationTypeKey, AnnotationTypeName } from '../../../utils/annotationIcons';

// Plural labels per granular type key (openings/structures expand into subtypes).
// Static <T> literals so they stay extractable for translation.
export const TypeLabel = ({ typeKey }: { typeKey: AnnotationTypeName }) => {
  switch (typeKey) {
    case 'ROOM':
      return <T _str="rooms" swc />;
    case 'AREA':
      return <T _str="areas" swc />;
    case 'DOOR':
      return <T _str="doors" swc />;
    case 'WINDOW':
      return <T _str="windows" swc />;
    case 'BEAM':
      return <T _str="beams" swc />;
    case 'COLUMN':
      return <T _str="columns" swc />;
    default:
      return <T _str={typeKey.toLowerCase()} swc />;
  }
};

export interface TypeFilter {
  /** Distinct granular keys present in the annotations */
  availableKeys: AnnotationTypeName[];
  selectedKeys: Set<AnnotationTypeName>;
  toggleKey: (key: AnnotationTypeName) => void;
  /** Identifiers/types of the currently-selected (visible) annotations, for PropertyPanel */
  visibleIdentifiers: string[];
  visibleTypes: AnnotationType[];
  visibleAnnotations: { identifier: string; type: AnnotationType }[];
  /** True when the visible set spans more than one granular key (doors+windows, room+door, ...).
   *  A homogeneous set (single type, or single subtype) is not treated as a group. */
  isGroupView: boolean;
}

/**
 * Resolves annotations to granular type keys (openings → doors/windows,
 * structures → beams/columns) and tracks which keys are selected.
 * Selection resets whenever the available set of keys changes.
 */
export function useTypeFilter(annotations: { identifier: string; type: string }[]): TypeFilter {
  const resolved = useMemo(() => annotations.map((a) => ({ identifier: a.identifier, type: a.type as AnnotationType, typeKey: getAnnotationTypeKey(a) })), [annotations]);

  const availableKeys = useMemo(() => Array.from(new Set(resolved.map((a) => a.typeKey))), [resolved]);
  const keySignature = availableKeys.join('|');

  const [selectedKeys, setSelectedKeys] = useState<Set<AnnotationTypeName>>(() => new Set(availableKeys));
  // Reset to all-selected when the available set changes (e.g. a different selection).
  // Adjusting during render (rather than in an effect) means consumers never observe the
  // stale selection for a frame, and it avoids the extra render an effect would queue.
  useOnValueChange(keySignature, () => setSelectedKeys(new Set(availableKeys)));

  const toggleKey = (key: AnnotationTypeName) =>
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const visible = useMemo(() => resolved.filter((a) => selectedKeys.has(a.typeKey)), [resolved, selectedKeys]);
  const visibleIdentifiers = useMemo(() => visible.map((a) => a.identifier), [visible]);
  const visibleTypes = useMemo(() => Array.from(new Set(visible.map((a) => a.type))), [visible]);
  const visibleAnnotations = useMemo(() => visible.map((a) => ({ identifier: a.identifier, type: a.type })), [visible]);
  const isGroupView = useMemo(() => new Set(visible.map((a) => a.typeKey)).size > 1, [visible]);

  return { availableKeys, selectedKeys, toggleKey, visibleIdentifiers, visibleTypes, visibleAnnotations, isGroupView };
}

interface TypeFilterButtonProps extends Pick<TypeFilter, 'availableKeys' | 'selectedKeys' | 'toggleKey'> {
  className?: string;
}

/** Filter icon that opens a popover of per-type (and per-subtype) checkboxes. */
const TypeFilterButton_: React.FC<TypeFilterButtonProps> = ({ availableKeys, selectedKeys, toggleKey, className }) => {
  // Active = a filter is applied, i.e. not every available key is selected.
  const isActive = selectedKeys.size < availableKeys.length;

  return (
    <Popover className={className} buttonClassName={`cursor-pointer ${isActive ? 'text-green-500 hover:text-green-600' : 'text-[#6F7173] dark:text-[#C2C6C8] hover:text-black dark:hover:text-neon-green-300'}`} buttonChild={<FilterIcon className="w-4 h-4" />}>
    {() => (
      <>
        {availableKeys.map((key) => (
          <label key={key} className="flex items-center gap-2 px-2 py-1 text-sm font-normal normal-case cursor-pointer text-black dark:text-white">
            <input
              type="checkbox"
              checked={selectedKeys.has(key)}
              onChange={() => toggleKey(key)}
              className="w-4 h-4 rounded cursor-pointer border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 checked:bg-neon-green-700 checked:border-neon-green-700 focus:ring-2 focus:ring-neon-green-500 dark:focus:ring-neon-green-600 dark:ring-offset-gray-800"
            />
            <TypeLabel typeKey={key} />
          </label>
        ))}
      </>
    )}
  </Popover>
  );
};

export const TypeFilterButton = memo(TypeFilterButton_);
